import { createContext, useContext, useState, ReactNode } from 'react';

interface AppError {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  timestamp: Date;
  action?: () => void;
  actionLabel?: string;
}

interface ErrorContextType {
  errors: AppError[];
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
  handleApiError: (error: any, context: string, retryAction?: () => void) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [errors, setErrors] = useState<AppError[]>([]);

  const addError = (error: Omit<AppError, 'id' | 'timestamp'>) => {
    const newError: AppError = {
      ...error,
      id: Date.now().toString(),
      timestamp: new Date(),
    };
    setErrors(prev => [...prev, newError]);
  };

  const removeError = (id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const handleApiError = (error: any, context: string, retryAction?: () => void) => {
    console.error(`${context} error:`, error);
    const errorMessage = error?.response?.data?.message || `Failed to ${context.toLowerCase()}. Please try again.`;
    addError({
      message: errorMessage,
      type: 'error',
      action: retryAction || (() => window.location.reload()),
      actionLabel: retryAction ? 'Retry' : 'Refresh Page'
    });
  };

  return (
    <ErrorContext.Provider value={{ errors, addError, removeError, clearErrors, handleApiError }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
}
